const TOKEN_LENGTH = 22;

/**
 * Removes the Helcim Pay iframe from the DOM and cleans up event listeners
 */
export function removeHelcimPayIframe(): void {
  const frame = document.getElementById('helcimPayIframe');
  if (frame instanceof HTMLIFrameElement) {
    frame.remove();
    window.removeEventListener('message', watchForExit, false);
  }
}

/**
 * Event listener to watch for iframe exit events
 * @param event - Message event from the iframe
 */
export function watchForExit(event: any): void {
  if (typeof event?.data?.eventName === 'string' && !event.data.eventName.includes('helcim-pay-js')) {
    return;
  }
  if (typeof event?.data?.eventStatus === 'string' && event?.data?.eventStatus === 'HIDE') {
    removeHelcimPayIframe();
  }
}

/**
 * Creates and appends a Helcim Pay iframe to the document body
 * @param checkoutToken - The checkout token from Helcim
 * @param showExitButton - Whether to show the exit button (default: true)
 * @param phone - Optional phone number
 * @param email - Optional email address
 * @param csrf - CSRF token for security
 * @returns The created iframe element
 */
export function appendHelcimPayIframe(
  checkoutToken: string, 
  showExitButton: boolean = true, 
  phone: string = '', 
  email: string = '', 
  csrf: string = ''
): HTMLIFrameElement {
  console.log(" TOKEN ", checkoutToken);
  
  // Determine environment (dev vs production)
  const dev = window.location.hostname.endsWith('.helcim.test') || window.location.hostname.endsWith('.myhelcim.test');
  const domain = !dev ? 'secure.helcim.app' : 'secure-app.helcim.test';
  
  // Build query parameters
  const hasExitButtonQuery = showExitButton ? '?allowExit' : '';
  const phoneQuery = phone ? `&phone=${phone}` : '';
  const emailQuery = email ? `&email=${email}` : '';
  const url = `https://${domain}/helcim-pay/${checkoutToken}${hasExitButtonQuery}${phoneQuery}${emailQuery}`;

  // Create iframe element
  const iframeElem = document.createElement('iframe');
  iframeElem.name = csrf;
  iframeElem.id = 'helcimPayIframe';
  iframeElem.src = url;
  iframeElem.width = '100%';
  iframeElem.height = '100%';
  iframeElem.style.border = 'none';
  (iframeElem as any).style = 'position: fixed; top: 0; left: 0; border: none; z-index: 998;';
  iframeElem.allow = 'payment';

  // Add event listener for iframe messages
  window.addEventListener('message', watchForExit, false);

  // Append to document body
  document.body.appendChild(iframeElem);
  return iframeElem;
}

/**
 * Sets up payment message event listener for Helcim payment completion
 * @param checkoutToken - The checkout token to listen for
 * @param onSuccess - Callback function when payment succeeds
 * @param onAborted - Callback function when payment is aborted
 * @param onHide - Callback function when payment is hidden/cancelled
 */
export function setupPaymentEventListener(
  checkoutToken: string,
  onSuccess?: (eventMessage: any) => void,
  onAborted?: (eventMessage: any) => void,
  onHide?: (eventMessage: any) => void
): (() => void) {
  const eventListener = (event: MessageEvent) => {
    const helcimPayJsIdentifierKey = 'helcim-pay-js-' + checkoutToken;

    if (event.data.eventName === helcimPayJsIdentifierKey) {
      console.log(">>> EVENT MESSAGE ", event.data);

      if (event.data.eventStatus === 'HIDE') {
        console.log('Transaction SKIPPED!', event.data.eventMessage);
        if (onHide) onHide(event.data.eventMessage);
      }

      if (event.data.eventStatus === 'ABORTED') {
        console.error('Transaction failed!', event.data.eventMessage);
        if (onAborted) onAborted(event.data.eventMessage);
      }

      if (event.data.eventStatus === 'SUCCESS') {
        console.log(">>> PAYMENT SUCCESS!");
        const eventMessage = JSON.parse(event.data.eventMessage).data.data;
        console.log('Transaction success!', eventMessage);
        
        if (onSuccess) onSuccess(eventMessage);
      }
    }
  };

  window.addEventListener('message', eventListener);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('message', eventListener);
  };
}

/**
 * Payment-related constants and UI text
 */
export const PAYMENT_CONSTANTS = {
  EMOJI_SIGNATURE: "✍️",
  EMOJI_DOCUMENT: "📄", 
  EMOJI_CHECK_MARK: "✅",
  EMOJI_CREDIT_CARD: "💳",
  
  PREP_AGREEMENT_TITLE: `📄 Please sign your storage agreement`,
  PREP_PAYMENT_TITLE: `💳 Please complete your secure payment`,
  PLEASE_WAIT: "Please wait ...",
  THANK_YOU_TITLE: `✅ Thanks! Your space is now reserved!`,
  PREP_PAYMENT_SUBTITLE: "Payment is required to finalize your reservation",
  RESERVATION_DETAILS_SUBTITLE: "You will receive reservation details by text and email",
  
  TOKEN_LENGTH
};

/**
 * Type definitions for payment-related data structures
 */
export interface PaymentEventData {
  transactionId: string;
  dateCreated: string;
  cardBatchId: string;
  status: string;
  type: string;
  amount: string;
  currency: string;
  avsResponse: string;
  cvvResponse: string;
  approvalCode: string;
  cardToken: string;
  cardNumber: string;
  cardHolderName: string;
  customerCode: string;
  invoiceNumber: string;
  warning: string;
}

export interface PaymentLineItem {
  sku: string;
  description: string;
  quantity: number;
  price: number;
  total: number;
}

export interface PaymentInvoiceData {
  lineItems: PaymentLineItem[];
  notes: string;
}